import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'; //-tech-debt-react-query;
import { CacheKey } from '../types/cache-keys';
import {
  CloneVmRequest,
  CreateNetworkRequest,
  CreateTopologyRequest,
  Topology,
  TopologyAccessRole,
  TopologyAccessRoleAssignment,
  TopologyCapabilities,
  TopologyCurrentUser,
  TopologyDatacenterCatalog,
  TopologyDatacenterDemo,
  TopologyDiagram,
  TopologyDiagramConnection,
  TopologyDiagramNode,
  TopologyLimits,
  TopologyNetwork,
  TopologyNodeDisplay,
  TopologyOverview,
  TopologyRemediation,
  TopologyTemplateTopology,
  TopologyValidationLimits,
  TopologyVm,
  cloneTopology,
  cloneTopologyVm,
  createDiagramBox,
  createDiagramConnection,
  createDiagramIcon,
  createDiagramText,
  createTopology,
  createTopologyNetwork,
  deleteDiagramConnection,
  deleteDiagramNode,
  deleteTopology,
  deleteTopologyNetwork,
  deleteTopologyVm,
  fetchDatacenterCatalog,
  fetchTopologies,
  fetchTopology,
  fetchTopologyCapabilities,
  fetchTopologyCollection,
  fetchTopologyCurrentUser,
  fetchTopologyDiagram,
  fetchTopologyDocumentation,
  fetchTopologyLimits,
  fetchTopologyOverview,
  fetchTopologyValidationLimits,
  grantTopologyAccess,
  isTopologyLinkError,
  isTopologyPasteRequired,
  pushTopologyToDcloud,
  revokeTopologyAccess,
  topologyCapabilitiesReady,
  topologyErrorRemediation,
  topologyErrorRemedy,
  updateDiagramNode,
  updateTopology,
  updateTopologyVm,
} from '../support/dcloudTopologyService';
import { DcloudLinkRemedy } from '../support/dcloudService';

/**
 * Topology Builder data is editable state a user is actively working on, so it is
 * short lived and refetched on focus. `retry: false` matters more here than
 * elsewhere: the common failure is a missing dCloud link, which retrying cannot
 * fix.
 */
const TOPOLOGY_QUERY_OPTIONS = {
  staleTime: 1000 * 30,
  gcTime: 1000 * 60 * 5,
  retry: false as const,
};

/** Catalogs of what can be added change rarely and are large. */
const TOPOLOGY_CATALOG_OPTIONS = {
  ...TOPOLOGY_QUERY_OPTIONS,
  staleTime: 1000 * 60 * 30,
};

export const useDcloudTopologyCapabilities = (
  options?: Record<string, any>,
): UseQueryResult<TopologyCapabilities, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologyCapabilities],
    queryFn: () => fetchTopologyCapabilities(),
    ...{
        ...TOPOLOGY_QUERY_OPTIONS,
        staleTime: 1000 * 60,
        ...options,
      },
  });

export const useDcloudTopologyCurrentUser = (
  options?: Record<string, any>,
): UseQueryResult<TopologyCurrentUser, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologyCurrentUser],
    queryFn: () => fetchTopologyCurrentUser(),
    ...{ ...TOPOLOGY_CATALOG_OPTIONS, ...options },
  });

export const useDcloudTopologyValidationLimits = (
  options?: Record<string, any>,
): UseQueryResult<TopologyValidationLimits, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologyValidationLimits],
    queryFn: () => fetchTopologyValidationLimits(),
    ...{ ...TOPOLOGY_CATALOG_OPTIONS, ...options },
  });

export const useDcloudTopologies = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<{ topologies: Topology[]; raw: any }, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologies, params],
    queryFn: () => fetchTopologies(params),
    ...{
        ...TOPOLOGY_QUERY_OPTIONS,
        refetchOnWindowFocus: true,
        ...options,
      },
  });

export const useDcloudTopology = (
  uid: string | null,
  options?: Record<string, any>,
): UseQueryResult<Topology, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopology, uid],
    queryFn: () => fetchTopology(uid as string),
    ...{
        ...TOPOLOGY_QUERY_OPTIONS,
        enabled: Boolean(uid),
        ...options,
      },
  });

export const useDcloudTopologyLimits = (
  uid: string | null,
  options?: Record<string, any>,
): UseQueryResult<TopologyLimits, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologyCollection, uid, 'limits'],
    queryFn: () => fetchTopologyLimits(uid as string),
    ...{
        ...TOPOLOGY_QUERY_OPTIONS,
        enabled: Boolean(uid),
        ...options,
      },
  });

/**
 * The whole configuration view in one call. Prefer this over one query per tab:
 * the backend fans the sub-resource requests out concurrently, and a section that
 * fails arrives as `{error}` beside the ones that worked.
 */
export const useDcloudTopologyOverview = (
  uid: string | null,
  options: {
    sections?: string[];
    inventory?: boolean;
    queryOptions?: Record<string, any>;
  } = {},
): UseQueryResult<TopologyOverview, unknown> => {
  const { sections, inventory, queryOptions } = options;

  return useQuery({
    queryKey: [CacheKey.DcloudTopologyOverview, uid, sections?.join(',') || 'all', Boolean(inventory)],
    queryFn: () => fetchTopologyOverview(uid as string, { sections, inventory }),
    ...{
        ...TOPOLOGY_QUERY_OPTIONS,
        enabled: Boolean(uid),
        ...queryOptions,
      },
  });
};

export const useDcloudTopologyCollection = <T = any>(
  uid: string | null,
  collection: string,
  options?: Record<string, any>,
): UseQueryResult<{ rows: T[]; raw: any }, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologyCollection, uid, collection],
    queryFn: () => fetchTopologyCollection<T>(uid as string, collection),
    ...{
        ...TOPOLOGY_QUERY_OPTIONS,
        enabled: Boolean(uid && collection),
        ...options,
      },
  });

export const useDcloudTopologyInventory = <T = any>(
  uid: string | null,
  collection: string,
  options?: Record<string, any>,
): UseQueryResult<{ rows: T[]; raw: any }, unknown> =>
  useDcloudTopologyCollection<T>(uid, collection, {
    ...TOPOLOGY_CATALOG_OPTIONS,
    ...options,
  });

export const useDcloudTopologyDiagram = (
  uid: string | null,
  options?: Record<string, any>,
): UseQueryResult<TopologyDiagram, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologyDiagram, uid],
    queryFn: () => fetchTopologyDiagram(uid as string),
    ...{
        ...TOPOLOGY_QUERY_OPTIONS,
        enabled: Boolean(uid),
        ...options,
      },
  });

export const useDcloudTopologyDocumentation = (
  uid: string | null,
  options?: Record<string, any>,
): UseQueryResult<any, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologyDocumentation, uid],
    queryFn: () => fetchTopologyDocumentation(uid as string),
    ...{
        ...TOPOLOGY_QUERY_OPTIONS,
        enabled: Boolean(uid),
        ...options,
      },
  });

/**
 * One data center's catalog of demos or templates.
 *
 * Keyed by data center as well as collection, because these answers are per data center and a
 * shared key would show London's demos while RTP is selected. Catalog lifetimes apply: dCloud
 * republishes a template occasionally, not while somebody is filling in a form.
 */
export const useDcloudDatacenterCatalog = <T = any>(
  dataCenter: string | null,
  catalog: TopologyDatacenterCatalog,
  options?: Record<string, any>,
): UseQueryResult<{ rows: T[]; raw: any }, unknown> =>
  useQuery({
    queryKey: [CacheKey.DcloudTopologyDatacenterCatalog, dataCenter, catalog],
    queryFn: () => fetchDatacenterCatalog<T>(dataCenter as string, catalog),
    ...{
        ...TOPOLOGY_CATALOG_OPTIONS,
        enabled: Boolean(dataCenter && catalog),
        ...options,
      },
  });

export const useDcloudDatacenterDemos = (
  dataCenter: string | null,
  options?: Record<string, any>,
): UseQueryResult<{ rows: TopologyDatacenterDemo[]; raw: any }, unknown> =>
  useDcloudDatacenterCatalog<TopologyDatacenterDemo>(dataCenter, 'inventory-demos', options);

export const useDcloudDatacenterTemplateTopologies = (
  dataCenter: string | null,
  options?: Record<string, any>,
): UseQueryResult<{ rows: TopologyTemplateTopology[]; raw: any }, unknown> =>
  useDcloudDatacenterCatalog<TopologyTemplateTopology>(
    dataCenter,
    'inventory-template-topologies',
    options,
  );

/**
 * Invalidation for the mutations below.
 *
 * Every write changes derived state somewhere else - adding a VM moves the CPU
 * and memory usage, adding a network changes what interfaces can attach to, and
 * anything at all clears inSyncWithDcloud - so a write refetches the whole
 * topology rather than trying to patch caches by hand.
 */
const useTopologyInvalidation = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (uid?: string | null) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudTopologies] });

      if (!uid) return;

      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudTopology, uid] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudTopologyOverview, uid] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudTopologyCollection, uid] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.DcloudTopologyDiagram, uid] });
    },
    [queryClient],
  );
};

export const useCreateDcloudTopology = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<Topology, Error, CreateTopologyRequest>({
    mutationFn: (payload) => createTopology(payload),
    onSuccess: (topology) => invalidate(topology?.uid),
  });
};

export const useUpdateDcloudTopology = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<Topology, Error, { uid: string; patch: Record<string, any> }>({
    mutationFn: ({ uid, patch }) => updateTopology(uid, patch),
    onSuccess: (_topology, variables) => invalidate(variables.uid),
  });
};

export const useDeleteDcloudTopology = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<void, Error, string>({
    mutationFn: (uid) => deleteTopology(uid),
    onSuccess: (_result, uid) => invalidate(uid),
  });
};

export const useCloneDcloudTopology = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<Topology, Error, { uid: string; name: string; description?: string }>({
    mutationFn: ({ uid, name, description }) => cloneTopology(uid, { name, description }),
    // The clone is a new topology, so both it and the list need refetching.
    onSuccess: (clone, variables) => {
      invalidate(variables.uid);
      invalidate(clone?.uid);
    },
  });
};

export const useCreateDcloudTopologyNetwork = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<TopologyNetwork, Error, CreateNetworkRequest>({
    mutationFn: (payload) => createTopologyNetwork(payload),
    onSuccess: (_network, variables) => invalidate(variables.topologyUid),
  });
};

export const useDeleteDcloudTopologyNetwork = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<void, Error, { uid: string; topologyUid: string }>({
    mutationFn: ({ uid }) => deleteTopologyNetwork(uid),
    onSuccess: (_result, variables) => invalidate(variables.topologyUid),
  });
};

export const useCloneDcloudTopologyVm = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<TopologyVm, Error, CloneVmRequest>({
    mutationFn: (payload) => cloneTopologyVm(payload),
    onSuccess: (_vm, variables) => invalidate(variables.topologyUid),
  });
};

export const useUpdateDcloudTopologyVm = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<
    TopologyVm,
    Error,
    { uid: string; topologyUid: string; patch: Record<string, any> }
  >({
    mutationFn: ({ uid, patch }) => updateTopologyVm(uid, patch),
    onSuccess: (_vm, variables) => invalidate(variables.topologyUid),
  });
};

export const useDeleteDcloudTopologyVm = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<void, Error, { uid: string; topologyUid: string }>({
    mutationFn: ({ uid }) => deleteTopologyVm(uid),
    onSuccess: (_result, variables) => invalidate(variables.topologyUid),
  });
};

/**
 * Drag-release on the diagram. Only the diagram cache is invalidated: a move
 * changes no counts, no limits and nothing on the other tabs.
 */
export const useMoveDcloudDiagramNode = () => {
  const queryClient = useQueryClient();

  return useMutation<
    TopologyDiagramNode,
    Error,
    { uid: string; topologyUid: string; display: TopologyNodeDisplay }
  >({
    mutationFn: ({ uid, display }) => updateDiagramNode(uid, display),
    onSuccess: (_node, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CacheKey.DcloudTopologyDiagram, variables.topologyUid],
      });
    },
  });
};

export type DiagramDrawingRequest =
  | { topologyUid: string; kind: 'text'; text?: string; x?: number; y?: number }
  | {
      topologyUid: string;
      kind: 'box';
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    }
  | {
      topologyUid: string;
      kind: 'icon';
      icon: string;
      name?: string;
      x1?: number;
      y1?: number;
    };

export const useCreateDcloudDiagramDrawing = () => {
  const queryClient = useQueryClient();

  return useMutation<TopologyDiagramNode, Error, DiagramDrawingRequest>({
    mutationFn: (request) => {
      if (request.kind === 'text') {
        return createDiagramText(request.topologyUid, request);
      }

      if (request.kind === 'box') {
        return createDiagramBox(request.topologyUid, request);
      }

      return createDiagramIcon(request.topologyUid, request);
    },
    onSuccess: (_node, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CacheKey.DcloudTopologyDiagram, variables.topologyUid],
      });
    },
  });
};

export const useDeleteDcloudDiagramNode = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<void, Error, { uid: string; topologyUid: string }>({
    mutationFn: ({ uid }) => deleteDiagramNode(uid),
    // A node can back a real asset, so this is not diagram-only.
    onSuccess: (_result, variables) => invalidate(variables.topologyUid),
  });
};

export const useCreateDcloudDiagramConnection = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<
    TopologyDiagramConnection,
    Error,
    { topologyUid: string; fromUid: string; toUid: string }
  >({
    mutationFn: ({ topologyUid, fromUid, toUid }) =>
      createDiagramConnection(topologyUid, fromUid, toUid),
    onSuccess: (_connection, variables) => invalidate(variables.topologyUid),
  });
};

export const useDeleteDcloudDiagramConnection = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<void, Error, { uid: string; topologyUid: string }>({
    mutationFn: ({ uid }) => deleteDiagramConnection(uid),
    onSuccess: (_result, variables) => invalidate(variables.topologyUid),
  });
};

/**
 * Push to dCloud. This is what makes saved edits visible to sessions, so the
 * topology is refetched afterwards to pick up inSyncWithDcloud.
 */
export const usePushDcloudTopology = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<any, Error, { topologyUid: string; demoId: number | string }>({
    mutationFn: ({ topologyUid, demoId }) => pushTopologyToDcloud({ demoId, topologyUid }),
    onSuccess: (_result, variables) => invalidate(variables.topologyUid),
  });
};

export const useGrantDcloudTopologyAccess = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<
    TopologyAccessRoleAssignment,
    Error,
    { topologyUid: string; email: string; accessRole?: TopologyAccessRole }
  >({
    mutationFn: (payload) => grantTopologyAccess(payload),
    onSuccess: (_assignment, variables) => invalidate(variables.topologyUid),
  });
};

export const useRevokeDcloudTopologyAccess = () => {
  const invalidate = useTopologyInvalidation();

  return useMutation<void, Error, { uid: string; topologyUid: string }>({
    mutationFn: ({ uid }) => revokeTopologyAccess(uid),
    onSuccess: (_result, variables) => invalidate(variables.topologyUid),
  });
};

/**
 * Whether a failed Topology Builder query needs a dCloud sign-in rather than a
 * retry. Re-exported so components do not import the service directly for it.
 */
export const isDcloudTopologyLinkError = isTopologyLinkError;

/** True when only a token pasted from a Topology Builder tab resolves the failure. */
export const isDcloudTopologyPasteRequired = isTopologyPasteRequired;

export const dcloudTopologyErrorRemedy = topologyErrorRemedy;

export type DcloudTopologyReadiness = TopologyRemediation & {
  /** Still deciding; render a spinner rather than either outcome. */
  loading: boolean;
  /** The backend has no Topology Builder routes at all. */
  unsupported: boolean;
  /** Turned off for this environment. Nothing the caller can do. */
  disabled: boolean;
  /** Requests can be expected to succeed. */
  ready: boolean;
  /** What the caller must do, when it is not already ready. */
  remedy?: DcloudLinkRemedy;
  /** True when signing in is genuinely the fix. */
  needsSignIn: boolean;
  /**
   * True when signing in is *not* the fix - the link is live but its token comes
   * from a provider TBv3 does not trust, so only a paste resolves it.
   */
  needsPaste: boolean;
  capabilities?: TopologyCapabilities;
  error?: unknown;
  refetch: () => void;
};

/**
 * One decision about whether Topology Builder is usable, and what to show if not.
 *
 * Exists because the answer has three inputs - the capabilities pre-flight, a live
 * query's error, and which of `sign_in`/`paste_token` applies - and every screen
 * that touches Topology Builder needs the same conclusion from them. Getting it
 * wrong in one place means telling a signed-in user to sign in again.
 *
 * `queryError` is any error from a real Topology Builder request. It matters
 * because a token can be accepted at pre-flight and expire moments later, and
 * because a pre-flight from a backend without `ready` reports healthy right up
 * until the first 401.
 */
export const useDcloudTopologyReadiness = (
  queryError?: unknown,
): DcloudTopologyReadiness => {
  const {
    data: capabilities,
    isLoading,
    isError,
    error,
    refetch,
  } = useDcloudTopologyCapabilities();

  const linkFailure = queryError && isTopologyLinkError(queryError);

  // A live failure outranks the pre-flight: it is the more recent evidence, and it
  // is the only signal available when the pre-flight predates `ready`.
  const remedy: DcloudLinkRemedy | undefined = linkFailure
    ? topologyErrorRemedy(queryError) || 'sign_in'
    : capabilities && !topologyCapabilitiesReady(capabilities)
      ? capabilities.remedy || 'sign_in'
      : undefined;

  const remediation: TopologyRemediation = linkFailure
    ? topologyErrorRemediation(queryError)
    : {
        remedy: capabilities?.remedy,
        login_url: capabilities?.login_url,
        adopt_url: capabilities?.adopt_url,
        topology_login_url: capabilities?.topology_login_url,
        token_hint: capabilities?.token_hint,
      };

  const disabled = Boolean(capabilities && !capabilities.enabled);

  return {
    ...remediation,
    remedy,
    loading: isLoading,
    unsupported: isError,
    disabled,
    ready: Boolean(
      !isLoading &&
        !isError &&
        topologyCapabilitiesReady(capabilities) &&
        !linkFailure,
    ),
    needsSignIn: remedy === 'sign_in',
    needsPaste: remedy === 'paste_token',
    capabilities,
    error: isError ? error : queryError,
    refetch: () => {
      void refetch();
    },
  };
};
